import { FC, useState } from 'react'
import Button from '@mui/material/Button'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListSubheader from '@mui/material/ListSubheader'
import ListItemText from '@mui/material/ListItemText'
import {
  availableNetworks,
  choose,
  NET_LABEL,
  network,
  networkEnv,
  VERIFY_LABEL,
  type NetworkChoice,
  type VerifyMode,
} from './network.js'

const NET_HINT: Record<NetworkChoice, string> = {
  devnet: 'Solana test network (test money)',
  mainnet: 'Real Solana network',
  localnet: 'The local validator',
}

const VERIFY_HINT: Record<VerifyMode, string> = {
  fixture: 'Scripted verification — nothing is checked',
  real: 'Really check the video against the chain',
}

/**
 * Picks which network this browser uses and whether verification is the
 * scripted demo or real. Same settings as `?net=` and `?verify=`. Changing
 * either reloads the page, because every program address is fixed at load
 * (see network.ts).
 *
 * `floating` is for the public player: it only renders once somebody has
 * deliberately picked something (a URL parameter or this menu), so an
 * ordinary viewer never sees it, while a tester always has a way back.
 */
const NetworkSwitch: FC<{ floating?: boolean }> = ({ floating = false }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  if (floating && !network.explicit) return null

  const close = () => setAnchor(null)

  return (
    <div style={floating ? { position: 'fixed', right: 12, top: 12, zIndex: 2000 } : undefined}>
      <Button
        size="small"
        variant={floating ? 'contained' : 'outlined'}
        color={network.net === 'mainnet' ? 'error' : network.demo ? 'inherit' : 'primary'}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
        title="Which network, and whether verification is real"
        sx={floating ? { opacity: 0.85, textTransform: 'none', minWidth: 0, px: 1.25, py: 0.25, fontSize: 12 } : { textTransform: 'none' }}
      >
        {NET_LABEL[network.net]} · {VERIFY_LABEL[network.verify]}
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
        <ListSubheader>Network</ListSubheader>
        {availableNetworks(networkEnv).map(({ choice, enabled, note }) => (
          <MenuItem
            key={choice}
            disabled={!enabled}
            selected={choice === network.net}
            onClick={() => {
              close()
              if (choice !== network.net) choose({ net: choice })
            }}
          >
            <ListItemText primary={NET_LABEL[choice]} secondary={note ?? NET_HINT[choice]} />
          </MenuItem>
        ))}
        <ListSubheader>Verification</ListSubheader>
        {(['fixture', 'real'] as const).map((mode) => (
          <MenuItem
            key={mode}
            selected={mode === network.verify}
            onClick={() => {
              close()
              if (mode !== network.verify) choose({ verify: mode })
            }}
          >
            <ListItemText primary={VERIFY_LABEL[mode]} secondary={VERIFY_HINT[mode]} />
          </MenuItem>
        ))}
      </Menu>
    </div>
  )
}

export default NetworkSwitch
